import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateSegment } from '@partful/entities/instructions/segment';
import { SegmentModel, SegmentWithContentModel } from '@partful/instructions-domain-models';
import { classNames } from '@partful/shared/lib/classNames';
import { Button } from '@partful/shared/ui/Button';
import { Input } from '@partful/shared/ui/Input';
import { Textarea } from '@partful/shared/ui/Textarea';
import { Typography } from '@partful/shared/ui/Typography';
import { ComponentProps } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './SegmentForm.module.scss';

type SegmentFormProps = {
  segment?: SegmentModel & Partial<SegmentWithContentModel>;
  onCancel?: () => void;
  onSubmit: SubmitHandler<UpdateSegment>;
  disabled?: boolean;
} & Omit<ComponentProps<'form'>, 'onSubmit'>;

export function SegmentForm({ segment, onCancel, onSubmit, disabled, className, ...rest }: Readonly<SegmentFormProps>) {
  const { t: i18n } = useTranslation();

  const { formState, handleSubmit, control } = useForm<UpdateSegment>({
    resolver: zodResolver(UpdateSegment),
    mode: 'onChange',
    values: {
      name: segment?.content?.name ?? '',
      description: segment?.content?.description ?? '',
    },
  });

  return (
    <form {...rest} className={classNames(styles.segmentForm, className)} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.control}>
        <Typography as="label" htmlFor={`segment-${segment?.order ?? 'new'}-name`} fontWeight={600}>
          {i18n('instructions:segment.data.name.label')} <sup>({i18n('instructions:common.required')})</sup>
        </Typography>
        <Controller
          name="name"
          control={control}
          render={({ field: { name, onBlur, onChange, value }, fieldState: { error } }) => (
            <>
              <Input
                autoFocus
                id={`segment-${segment?.order ?? 'new'}-name`}
                name={name}
                placeholder={i18n('instructions:segment.data.name.placeholder')}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                disabled={disabled}
              />
              {error && (
                <Typography appearance="label" className={styles.errorMessage}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                  {i18n(
                    /* istanbul ignore next - very defensive fallback case */ error.message ??
                      'instructions:segment.data.name.validation.generic'
                  )}
                </Typography>
              )}
            </>
          )}
        />
      </div>
      <div className={styles.control}>
        <Typography as="label" htmlFor={`segment-${segment?.order ?? 'new'}-description`} fontWeight={600}>
          {i18n('instructions:segment.data.description.label')} <sup>&nbsp;</sup>
        </Typography>
        <Controller
          name="description"
          control={control}
          render={({ field: { name, onBlur, onChange, value }, fieldState: { error } }) => (
            <>
              <Textarea
                id={`segment-${segment?.order ?? 'new'}-description`}
                name={name}
                placeholder={i18n('instructions:segment.data.description.placeholder')}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                disabled={disabled}
                maxRows={3}
              />
              {error && (
                <Typography appearance="label" className={styles.errorMessage}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                  {i18n(
                    /* istanbul ignore next - very defensive fallback case */ error.message ??
                      'instructions:segment.data.description.validation.generic'
                  )}
                </Typography>
              )}
            </>
          )}
        />
      </div>
      <div className={styles.actions}>
        <Button
          name="instructions_version_langandinstructions_save_segment_click"
          variant="primary"
          type="submit"
          disabled={!formState.isValid || !formState.isDirty || disabled}
        >
          {i18n('instructions:common.save')}
        </Button>
        <Button
          name="instructions_version_langandinstructions_cancel_save_segment_click"
          variant="tertiary"
          type="button"
          onClick={onCancel}
          disabled={disabled}
        >
          {i18n('instructions:common.cancel')}
        </Button>
      </div>
    </form>
  );
}
